/**
 * Delegation Management - UI for managing approval delegations
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { 
  Plus, 
  UserCheck,
  ArrowRight,
  Calendar,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import { FioriPageHeader } from "@/components/fiori";

export function DelegationManagement() {
  const { t } = useTranslation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Queries
  const { data: myDelegations, refetch: refetchMy } = trpc.workflows.delegations.myDelegations.useQuery();
  const { data: allUsers } = trpc.users.list.useQuery({});
  const { data: me } = trpc.auth.me.useQuery();

  // Mutations
  const createDelegation = trpc.workflows.delegations.create.useMutation({
    onSuccess: () => {
      toast.success(t('delegations.delegationCreated', 'Delegation created successfully'));
      setIsCreateDialogOpen(false);
      refetchMy();
    },
    onError: (error) => toast.error(error.message),
  });

  const revokeDelegation = trpc.workflows.delegations.revoke.useMutation({
    onSuccess: () => {
      toast.success(t('delegations.delegationRevoked', 'Delegation revoked'));
      refetchMy();
    },
    onError: (error) => toast.error(error.message),
  });

  // Form state
  const [newDelegation, setNewDelegation] = useState({
    delegateId: 0,
    delegationType: "full" as "full" | "partial",
    validFrom: "",
    validUntil: "",
    reason: "",
  });

  const handleCreateDelegation = () => {
    if (!newDelegation.delegateId) {
      toast.error(t('delegations.selectDelegate', 'Please select a delegate'));
      return;
    }
    if (!newDelegation.validFrom || !newDelegation.validUntil) {
      toast.error(t('delegations.selectDates', 'Please select start and end dates'));
      return;
    }
    createDelegation.mutate({
      delegateId: newDelegation.delegateId,
      delegationType: newDelegation.delegationType,
      validFrom: newDelegation.validFrom,
      validUntil: newDelegation.validUntil,
      reason: newDelegation.reason || undefined,
    });
  };

  const getDelegationStatus = (delegation: any) => {
    const now = new Date();
    const start = new Date(delegation.validFrom);
    const end = new Date(delegation.validUntil);
    
    if (!delegation.isActive) return { label: t('delegations.statusRevoked', 'Revoked'), variant: "destructive" as const };
    if (now < start) return { label: t('delegations.statusScheduled', 'Scheduled'), variant: "secondary" as const };
    if (now > end) return { label: t('common.expired', 'Expired'), variant: "outline" as const };
    return { label: t('common.active', 'Active'), variant: "default" as const };
  };

  const asDelegator = myDelegations?.asDelegator || [];
  const asDelegate = myDelegations?.asDelegate || [];

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      {/* SAP Fiori Page Header */}
      <FioriPageHeader
        title={t('delegations.delegationManagement', 'Delegation Management')}
        subtitle={t('delegations.delegationManagementSubtitle', 'Delegate your approval authority to other users')}
        icon={<UserCheck className="h-5 w-5" />}
        actions={
          <Button className="bg-[#5B2C93] hover:bg-[#3D1C5E] gap-2" size="sm" onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4" /> {t('delegations.createDelegation', 'Create Delegation')}
          </Button>
        }
      />

      {/* KPI Strip */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        {[
          { label: t('delegations.activeDelegations', 'Active Delegations'), value: asDelegator.filter((d: any) => d.isActive).length, icon: ArrowRight, color: "#5B2C93", bg: "#E8DCF5" },
          { label: t('delegations.delegatedToMe', 'Delegated to Me'), value: asDelegate.filter((d: any) => d.isActive).length, icon: UserCheck, color: "#059669", bg: "#D1FAE5" },
          { label: t('delegations.statusScheduled', 'Scheduled'), value: asDelegator.filter((d: any) => { const now = new Date(); const start = new Date(d.validFrom); return d.isActive && now < start; }).length, icon: Clock, color: "#D97706", bg: "#FEF3C7" },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white border border-[#E0E0E0] rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: kpi.bg }}>
                <kpi.icon className="h-4 w-4" style={{ color: kpi.color }} />
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B] font-medium">{kpi.label}</p>
                <p className="text-xl font-semibold text-[#2C2C2C]">{kpi.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* My Delegations */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('delegations.myDelegations', 'My Delegations')}</CardTitle>
          <CardDescription>{t('delegations.myDelegationsDesc', 'Approval authority you have delegated to others')}</CardDescription>
        </CardHeader>
        <CardContent>
          {asDelegator.length === 0 ? (
            <div className="text-center py-8 text-[#6B6B6B]">
              <UserCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('delegations.noDelegations', "You haven't created any delegations")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('delegations.delegate', 'Delegate')}</TableHead>
                  <TableHead>{t('common.type', 'Type')}</TableHead>
                  <TableHead>{t('delegations.period', 'Period')}</TableHead>
                  <TableHead>{t('delegations.reason', 'Reason')}</TableHead>
                  <TableHead>{t('common.status', 'Status')}</TableHead>
                  <TableHead className="w-[100px]">{t('common.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {asDelegator.map((delegation: any) => {
                  const status = getDelegationStatus(delegation);
                  return (
                    <TableRow key={delegation.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{delegation.delegateName} {delegation.delegateLastName}</p>
                          <p className="text-sm text-[#6B6B6B]">{delegation.delegateEmail}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {delegation.delegationType === "full" ? t('delegations.fullAuthority', 'Full Authority') : t('delegations.partial', 'Partial')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{format(new Date(delegation.validFrom), "MMM d, yyyy")}</p>
                          <p className="text-[#6B6B6B]">
                             {t('common.to', 'to')} {format(new Date(delegation.validFrom), "MMM d, yyyy")}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{delegation.reason || "-"}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </TableCell>
                      <TableCell>
                        {delegation.isActive && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => revokeDelegation.mutate({ id: delegation.id })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delegations to Me */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('delegations.delegatedToMe', 'Delegated to Me')}</CardTitle>
          <CardDescription>{t('delegations.delegatedToMeDesc', 'Approval authority others have delegated to you')}</CardDescription>
        </CardHeader>
        <CardContent>
          {asDelegate.length === 0 ? (
            <div className="text-center py-8 text-[#6B6B6B]">
              <ArrowRight className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('delegations.noDelegatedToMe', 'No one has delegated authority to you')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('delegations.delegator', 'Delegator')}</TableHead>
                  <TableHead>{t('common.type', 'Type')}</TableHead>
                  <TableHead>{t('delegations.period', 'Period')}</TableHead>
                  <TableHead>{t('delegations.reason', 'Reason')}</TableHead>
                  <TableHead>{t('common.status', 'Status')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {asDelegate.map((delegation: any) => {
                  const status = getDelegationStatus(delegation);
                  return (
                    <TableRow key={delegation.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{delegation.delegatorName} {delegation.delegatorLastName}</p>
                          <p className="text-sm text-[#6B6B6B]">{delegation.delegatorEmail}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {delegation.delegationType === "full" ? t('delegations.fullAuthority', 'Full Authority') : t('delegations.partial', 'Partial')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{format(new Date(delegation.validFrom), "MMM d, yyyy")}</p>
                          <p className="text-[#6B6B6B]">
                            {t('common.to', 'to')} {format(new Date(delegation.validUntil), "MMM d, yyyy")}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{delegation.reason || "-"}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Delegation Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('delegations.createDelegation', 'Create Delegation')}</DialogTitle>
            <DialogDescription>
              {t('delegations.createDelegationDesc', 'Delegate your approval authority to another user')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('delegations.delegateTo', 'Delegate To')} *</Label>
              <Select
                value={newDelegation.delegateId.toString()}
                onValueChange={(v) => setNewDelegation({ ...newDelegation, delegateId: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('common.selectUser', 'Select user')} />
                </SelectTrigger>
                <SelectContent className="max-h-52 overflow-y-auto">
                  {allUsers?.users?.filter((u: any) => u.id !== me?.id).map((u: any) => (
                    <SelectItem key={u.id} value={u.id.toString()}>
                      {u.firstName} {u.lastName} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('delegations.delegationType', 'Delegation Type')}</Label>
              <Select
                value={newDelegation.delegationType}
                onValueChange={(v) => setNewDelegation({ ...newDelegation, delegationType: v as any })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">{t('delegations.fullAuthority', 'Full Authority')}</SelectItem>
                  <SelectItem value="partial">{t('delegations.partial', 'Partial')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('delegations.startDate', 'Start Date')} *</Label>
                <Input
                  type="date"
                  value={newDelegation.validFrom}
                  onChange={(e) => setNewDelegation({ ...newDelegation, validFrom: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('delegations.endDate', 'End Date')} *</Label>
                <Input
                  type="date"
                  value={newDelegation.validUntil}
                  onChange={(e) => setNewDelegation({ ...newDelegation, validUntil: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('delegations.reason', 'Reason')}</Label>
              <Input
                placeholder={t('delegations.reasonPlaceholder', 'e.g., Out of office, vacation')}
                value={newDelegation.reason}
                onChange={(e) => setNewDelegation({ ...newDelegation, reason: e.target.value })}
              />
            </div>

            <div className="bg-[#FEF3C7] border border-[#D97706] rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-[#D97706] shrink-0 mt-0.5" />
                <div className="text-sm text-[#D97706]">
                  <p className="font-medium">{t('common.important', 'Important')}</p>
                  <p>{t('delegations.delegateWarning', 'The delegate will be able to approve or reject requests on your behalf during the specified period.')}</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateDelegation} disabled={createDelegation.isPending}>
              {createDelegation.isPending ? t('common.creating', 'Creating...') : t('delegations.createDelegation', 'Create Delegation')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
